import { useState, useEffect } from "react";
import { Ava } from "../atoms/Ava.jsx";
import { CopySlug } from "../atoms/CopySlug.jsx";
import { getCandidateAnalytics, getQuestionClusters } from "../services/candidates.js";

/** Simple SVG bar chart for mood timeline */
function MoodChart({ timeline }) {
  if (!timeline?.length) return <div style={{color:"#334155",textAlign:"center",padding:"14px 0",fontSize:13}}>No mood data yet.</div>;

  const maxVal = Math.max(...timeline.map(d => d.positive + d.neutral + d.negative), 1);
  const barW = Math.max(8, Math.min(24, Math.floor(280 / timeline.length)));
  const chartH = 120;

  return (
    <div style={{overflowX:"auto"}}>
      <svg width={timeline.length * (barW + 4) + 20} height={chartH + 30} style={{display:"block"}}>
        {timeline.map((d, i) => {
          const total = d.positive + d.neutral + d.negative;
          const x = i * (barW + 4) + 10;
          const posH = (d.positive / maxVal) * chartH;
          const neuH = (d.neutral / maxVal) * chartH;
          const negH = (d.negative / maxVal) * chartH;
          return (
            <g key={d.date}>
              <title>{d.date}: +{d.positive} ~{d.neutral} -{d.negative}</title>
              <rect x={x} y={chartH - posH - neuH - negH} width={barW} height={negH} fill="#ef4444" rx={2}/>
              <rect x={x} y={chartH - posH - neuH} width={barW} height={neuH} fill="#f59e0b" rx={0}/>
              <rect x={x} y={chartH - posH} width={barW} height={posH} fill="#22c55e" rx={2}/>
              {i % Math.max(1, Math.floor(timeline.length / 6)) === 0 && (
                <text x={x + barW / 2} y={chartH + 14} textAnchor="middle" fill="#475569" fontSize={8}>{d.date.slice(5)}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function Analytics({persona, onBack, showOnly}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clusters, setClusters] = useState(null);
  const [clustersTotal, setClustersTotal] = useState(0);
  const [clustersPage, setClustersPage] = useState(0);
  const CLUSTERS_LIMIT = 20;
  const [drillCluster, setDrillCluster] = useState(null); // ANA-1: cluster drill-down

  useEffect(() => {
    getCandidateAnalytics(persona.id)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [persona.id]);

  useEffect(() => {
    getQuestionClusters(persona.id, CLUSTERS_LIMIT, clustersPage * CLUSTERS_LIMIT)
      .then(d => { setClusters(d?.clusters ?? []); setClustersTotal(d?.total ?? 0); })
      .catch(() => setClusters([]));
  }, [persona.id, clustersPage]);

  // Fallback to in-memory data while API loads
  const uMsgs = persona.conversations?.flatMap(c=>c.messages?.filter(m=>m.role==="user")) ?? [];
  const fc = data?.feedbackCounts ?? { liked: 0, disliked: 0, flagged: 0 };
  if (!data?.feedbackCounts) {
    (persona.feedback ?? []).forEach(f=>{fc[f.type === "notFactual" ? "flagged" : f.type]=(fc[f.type === "notFactual" ? "flagged" : f.type]||0)+1;});
  }

  const sb = data?.sentimentBreakdown ?? {positive:0,neutral:0,negative:0};
  const tot = (sb.positive + sb.neutral + sb.negative) || 1;
  const ov = sb.positive > sb.negative ? "Positive" : sb.negative > sb.positive ? "Negative" : "Neutral";
  const oc = ov === "Positive" ? "#22c55e" : ov === "Negative" ? "#ef4444" : "#f59e0b";

  const sessions = data?.totalSessions ?? persona.conversations?.length ?? 0;
  const questions = data?.totalQuestions ?? uMsgs.length;
  const likes = fc.liked ?? 0;

  return (
    <div style={{padding:"24px 28px",maxWidth:900}}>
      <div style={{marginBottom:28}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"#64748b",marginBottom:6}}>{persona.name}</div>
        <h1 style={{fontSize:24,fontWeight:800,color:"#f1f5f9",fontFamily:"Georgia,serif",margin:0}}>
          {showOnly === "clusters" ? "Question Clusters" : "Analytics"}
        </h1>
      </div>

      {loading && <div style={{textAlign:"center",color:"#64748b",padding:"32px 0"}}>Loading…</div>}

      {!showOnly && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
        {[[sessions,"Sessions","#3b82f6"],[questions,"Questions","#8b5cf6"],[ov,"Overall Mood",oc],[likes+"👍","Likes","#22c55e"]].map(([val,label,color])=>(
          <div key={label} style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:11,padding:16}}>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase",marginBottom:8}}>{label}</div>
            <div style={{fontSize:24,fontWeight:900,color,fontFamily:"Georgia,serif",lineHeight:1}}>{val}</div>
          </div>
        ))}
      </div>}

      {/* Extra stats row */}
      {!showOnly && data && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
          <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:11,padding:14,textAlign:"center"}}>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase",marginBottom:6}}>Unique Citizens</div>
            <div style={{fontSize:20,fontWeight:900,color:"#60a5fa",fontFamily:"Georgia,serif"}}>{data.uniqueVoters}</div>
          </div>
          <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:11,padding:14,textAlign:"center"}}>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase",marginBottom:6}}>Avg CP Score</div>
            <div style={{fontSize:20,fontWeight:900,color:"#8b5cf6",fontFamily:"Georgia,serif"}}>{data.avgCpScore}</div>
          </div>
          <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:11,padding:14,textAlign:"center"}}>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase",marginBottom:6}}>Total Sessions</div>
            <div style={{fontSize:20,fontWeight:900,color:"#3b82f6",fontFamily:"Georgia,serif"}}>{data.totalSessions}</div>
          </div>
        </div>
      )}

      {/* Sentiment breakdown */}
      {!showOnly && <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:13,padding:20,marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:700,color:"#94a3b8",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.08em"}}>Citizen Sentiment</div>
        {tot <= 1 && !data
          ? <div style={{color:"#334155",textAlign:"center",padding:"14px 0",fontSize:13}}>No conversations yet.</div>
          : <>
            <div style={{display:"flex",height:22,borderRadius:5,overflow:"hidden",marginBottom:12}}>
              {[["positive","#22c55e"],["neutral","#f59e0b"],["negative","#ef4444"]].map(([k,c])=>sb[k]>0&&<div key={k} style={{flex:sb[k],background:c}}/>)}
            </div>
            <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
              {[["positive","#22c55e","Positive"],["neutral","#f59e0b","Neutral"],["negative","#ef4444","Critical"]].map(([k,c,l])=>(
                <div key={k} style={{display:"flex",alignItems:"center",gap:5,fontSize:12}}>
                  <span style={{width:8,height:8,borderRadius:2,background:c,display:"inline-block"}}/>
                  <span style={{color:"#64748b"}}>{l}</span>
                  <strong style={{color:c}}>{Math.round(sb[k]/tot*100)}%</strong>
                </div>
              ))}
            </div>
          </>
        }
      </div>}

      {/* Mood over time chart */}
      {!showOnly && data?.moodTimeline?.length > 0 && (
        <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:13,padding:20,marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:700,color:"#94a3b8",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.08em"}}>Mood Over Time</div>
          <MoodChart timeline={data.moodTimeline}/>
          <div style={{display:"flex",gap:14,marginTop:10,justifyContent:"center"}}>
            {[["#22c55e","Positive"],["#f59e0b","Neutral"],["#ef4444","Negative"]].map(([c,l])=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:4,fontSize:10}}>
                <span style={{width:8,height:8,borderRadius:2,background:c,display:"inline-block"}}/>
                <span style={{color:"#64748b"}}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feedback */}
      {!showOnly && <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:13,padding:20,marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:700,color:"#94a3b8",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.08em"}}>Feedback</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          {[["👍 Liked",fc.liked,"#22c55e"],["👎 Disliked",fc.disliked,"#ef4444"],["⚠️ Flagged",fc.flagged,"#f59e0b"]].map(([label,count,color])=>(
            <div key={label} style={{background:color+"0d",border:`1px solid ${color}22`,borderRadius:9,padding:"13px 8px",textAlign:"center"}}>
              <div style={{fontSize:22,fontWeight:900,color,fontFamily:"Georgia,serif"}}>{count}</div>
              <div style={{fontSize:10,color:"#64748b",marginTop:3}}>{label}</div>
            </div>
          ))}
        </div>
      </div>}

      {/* ADMIN-8 + ANA-1: Question Clusters with drill-down */}
      <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:13,padding:20}}>
        {drillCluster ? (
          /* ── ANA-1: Drill-down view ────────────────────────────── */
          <div>
            <button onClick={() => setDrillCluster(null)} style={{background:"none",border:"none",color:"#60a5fa",cursor:"pointer",fontSize:12,fontFamily:"inherit",padding:0,marginBottom:14}}>← Back to clusters</button>
            <div style={{fontSize:13,fontWeight:700,color:"#e2e8f0",marginBottom:12,lineHeight:1.5}}>{drillCluster.representative}</div>
            <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
              <span style={{fontSize:10,fontWeight:700,color:"#60a5fa",background:"#3b82f618",padding:"3px 8px",borderRadius:5}}>{drillCluster.count} questions</span>
              <span style={{fontSize:10,color:"#8b5cf6"}}>CP: {drillCluster.avgCpScore}</span>
            </div>
            {/* Sentiment stacked bar */}
            {(() => {
              const sb = drillCluster.sentimentBreakdown ?? { positive: 0, neutral: 0, negative: 0 };
              const total = sb.positive + sb.neutral + sb.negative || 1;
              return (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",marginBottom:6}}>Sentiment Breakdown</div>
                  <div style={{display:"flex",height:10,borderRadius:5,overflow:"hidden",background:"#1e2d45"}}>
                    {sb.positive > 0 && <div style={{width:`${(sb.positive/total)*100}%`,background:"#22c55e"}} />}
                    {sb.neutral > 0 && <div style={{width:`${(sb.neutral/total)*100}%`,background:"#f59e0b"}} />}
                    {sb.negative > 0 && <div style={{width:`${(sb.negative/total)*100}%`,background:"#ef4444"}} />}
                  </div>
                  <div style={{display:"flex",gap:14,marginTop:6}}>
                    <span style={{fontSize:10,color:"#22c55e"}}>😊 {sb.positive}</span>
                    <span style={{fontSize:10,color:"#f59e0b"}}>😐 {sb.neutral}</span>
                    <span style={{fontSize:10,color:"#ef4444"}}>😞 {sb.negative}</span>
                  </div>
                </div>
              );
            })()}
            {/* All questions in cluster with per-question sentiment */}
            <div style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",marginBottom:6}}>All Questions ({drillCluster.questions?.length ?? 0})</div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {(drillCluster.questionDetails ?? drillCluster.questions ?? drillCluster.sampleQuestions ?? []).map((item, qi) => {
                const q = typeof item === "string" ? item : item.text;
                const s = typeof item === "string" ? null : item.sentiment;
                const dotColor = s === "positive" ? "#22c55e" : s === "negative" ? "#ef4444" : s === "neutral" ? "#f59e0b" : "#475569";
                return (
                  <div key={qi} style={{fontSize:12,color:"#94a3b8",padding:"6px 10px",background:"#090e1a",borderRadius:7,lineHeight:1.5,display:"flex",alignItems:"center",gap:8}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:dotColor,flexShrink:0}} />
                    <span style={{flex:1}}>{q}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ── Cluster list ────────────────────────────────────── */
          <>
            <div style={{fontSize:11,fontWeight:700,color:"#94a3b8",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.08em"}}>Question Clusters</div>
            {clusters === null ? (
              <div style={{color:"#475569",fontSize:13,textAlign:"center",padding:"14px 0"}}>Loading clusters…</div>
            ) : clusters.length === 0 ? (
              <div style={{color:"#334155",fontSize:13,textAlign:"center",padding:"14px 0"}}>No question clusters yet. Clusters appear after citizens ask several questions.</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {clusters.map((cl, i) => {
                  const insightColor = cl.insight === "well_covered" ? "#22c55e" : cl.insight === "moderate" ? "#f59e0b" : "#ef4444";
                  const insightLabel = cl.insight === "well_covered" ? "Well covered" : cl.insight === "moderate" ? "Moderate" : "Needs KB";
                  /* ANA-1: Mini sentiment bar on collapsed card */
                  const sb = cl.sentimentBreakdown ?? { positive: 0, neutral: 0, negative: 0 };
                  const sbTotal = sb.positive + sb.neutral + sb.negative || 1;
                  return (
                    <div key={i}>
                      <button onClick={() => setDrillCluster(cl)}
                        style={{width:"100%",padding:"12px 14px",background:"#090e1a",border:"1px solid #1e2d45",borderRadius:10,cursor:"pointer",textAlign:"left",fontFamily:"inherit",display:"flex",alignItems:"center",gap:10}}>
                        <div style={{flex:1}}>
                          <div style={{color:"#e2e8f0",fontSize:13,lineHeight:1.5}}>{cl.representative}</div>
                          <div style={{display:"flex",gap:10,marginTop:4,alignItems:"center"}}>
                            <span style={{fontSize:10,fontWeight:700,color:"#60a5fa",background:"#3b82f618",padding:"2px 7px",borderRadius:4}}>{cl.count}×</span>
                            <span style={{fontSize:10,color:"#8b5cf6"}}>CP: {cl.avgCpScore}</span>
                            <span style={{fontSize:10,fontWeight:600,color:insightColor,background:insightColor+"14",padding:"2px 7px",borderRadius:4}}>{insightLabel}</span>
                          </div>
                          {/* ANA-1: Mini sentiment bar */}
                          <div style={{display:"flex",height:4,borderRadius:2,overflow:"hidden",background:"#1e2d45",marginTop:6,maxWidth:120}}>
                            {sb.positive > 0 && <div style={{width:`${(sb.positive/sbTotal)*100}%`,background:"#22c55e"}} />}
                            {sb.neutral > 0 && <div style={{width:`${(sb.neutral/sbTotal)*100}%`,background:"#f59e0b"}} />}
                            {sb.negative > 0 && <div style={{width:`${(sb.negative/sbTotal)*100}%`,background:"#ef4444"}} />}
                          </div>
                        </div>
                        <span style={{color:"#475569",fontSize:12}}>→</span>
                      </button>
                    </div>
                  );
                })}
                {/* Clusters pagination */}
                {clustersTotal > CLUSTERS_LIMIT && (
                  <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:14}}>
                    <button onClick={() => setClustersPage(p => Math.max(0, p - 1))} disabled={clustersPage === 0}
                      style={{padding:"6px 14px",borderRadius:8,border:"1px solid #1e2d45",background:clustersPage === 0 ? "#0d1724" : "#1e2d45",color:clustersPage === 0 ? "#475569" : "#e2e8f0",fontSize:12,cursor:clustersPage === 0 ? "default" : "pointer"}}>
                      Prev
                    </button>
                    <span style={{color:"#64748b",fontSize:12,padding:"6px 0"}}>
                      {clustersPage * CLUSTERS_LIMIT + 1}–{Math.min((clustersPage + 1) * CLUSTERS_LIMIT, clustersTotal)} of {clustersTotal}
                    </span>
                    <button onClick={() => setClustersPage(p => p + 1)} disabled={(clustersPage + 1) * CLUSTERS_LIMIT >= clustersTotal}
                      style={{padding:"6px 14px",borderRadius:8,border:"1px solid #1e2d45",background:(clustersPage + 1) * CLUSTERS_LIMIT >= clustersTotal ? "#0d1724" : "#1e2d45",color:(clustersPage + 1) * CLUSTERS_LIMIT >= clustersTotal ? "#475569" : "#e2e8f0",fontSize:12,cursor:(clustersPage + 1) * CLUSTERS_LIMIT >= clustersTotal ? "default" : "pointer"}}>
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Analytics;
